import { Avatar, Badge, Box, Card, Menu, Text } from '@mantine/core'
import { IconChevronDown, IconPencil, IconTrash } from '@tabler/icons-react'
import { Button } from 'components/Button'
import { getInitials } from 'helpers'
import { useTranslation } from 'react-i18next'
import type { ManagedFaculty } from '../types'

interface Props {
  faculty: ManagedFaculty
  onEdit: (faculty: ManagedFaculty) => void
  onRemove: (faculty: ManagedFaculty) => void
}

export function FacultyCard({ faculty, onEdit, onRemove }: Props) {
  const { t } = useTranslation()

  const canRemove = faculty.permissions.remove

  return (
    <Card
      withBorder
      p='sm'
      radius='md'
      sx={theme => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing.xs,
        minHeight: 240,
      })}
    >
      <Avatar src={faculty.avatarUrl} size={48} radius='xl' color='gray.5'>
        {getInitials(`${faculty.firstName} ${faculty.lastName}`)}
      </Avatar>

      <Text weight={600} size='sm' align='center' lineClamp={1}>
        {faculty.firstName} {faculty.lastName}
      </Text>

      {faculty.email && (
        <Text size='xs' color='dimmed' align='center' lineClamp={1}>
          {faculty.email}
        </Text>
      )}

      {faculty.status === 'pending_confirmation' && (
        <Badge size='xs' color='yellow' variant='light'>
          {t('manage_faculty.pending_confirmation')}
        </Badge>
      )}

      <Box sx={{ marginTop: 'auto', width: '100%' }}>
        <Menu shadow='md' width={200} position='bottom' withinPortal>
          <Menu.Target>
            <Button fullWidth variant='light' rightIcon={<IconChevronDown size={18} stroke={1.5} />} pr={12}>
              {t('manage_faculty.actions')}
            </Button>
          </Menu.Target>

          <Menu.Dropdown>
            {faculty.permissions.editProfile && (
              <Menu.Item icon={<IconPencil size={16} />} onClick={() => onEdit(faculty)}>
                {t('manage_faculty.action_edit_profile')}
              </Menu.Item>
            )}
            {canRemove && (
              <Menu.Item icon={<IconTrash size={16} />} color='red' onClick={() => onRemove(faculty)}>
                {t('manage_faculty.action_remove')}
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </Box>
    </Card>
  )
}
